import { Time } from '@eagle/common';
import { DtcFaultDescriptionsResponseItem, DtcSourceDescriptionResponse } from '@eagle/core-data-types';
import { DtcMatchTypes, FaultUpdate } from '@eagle/data-function-types';
import { useQueries } from '@tanstack/react-query';
import { chunk, groupBy, mapValues } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../auth';
import { isNewEvent } from './events-table.queries';

interface BatchFaultDescriptionParams {
  eventDataList: Array<{ eventData: FaultUpdate; thingId: string; eventId: string }>;
}

interface BatchFaultDescriptionResult {
  [brandVariantId: number]: DtcFaultDescriptionsResponseItem | null;
}

interface BatchSourceDescriptionParams {
  eventDataList: Array<{ eventData: FaultUpdate; thingId: string; eventId: string }>;
}

interface BatchSourceDescriptionResult {
  [sourceId: number]: DtcSourceDescriptionResponse | null;
}

export const useBatchDtcFaultDescriptions = ({
  eventDataList,
}: BatchFaultDescriptionParams): {
  data: BatchFaultDescriptionResult;
  isLoading: boolean;
  isError: boolean;
} => {
  const { restClient } = useAuthenticated();
  const { i18n } = useTranslation();
  const i18nLang = i18n.resolvedLanguage?.split('-') || ['en'];
  const language = i18nLang[0].toLowerCase();

  const j1939Events = useMemo(() => eventDataList.filter(({ eventData }) => {
    return isNewEvent(eventData) &&
      eventData.dtcBrandVariantId !== null &&
      (eventData.dtcMatchType === DtcMatchTypes.EXACT || eventData.dtcMatchType === DtcMatchTypes.J1939);
  }), [eventDataList]);

  const groupedByBrand = useMemo(() => j1939Events.reduce<Record<string, Array<{ eventData: FaultUpdate & { dtcBrandVariantId: number; dtcBrandCode: string | null }; thingId: string; eventId: string }>>>((acc, item) => {
    const eventData = item.eventData as FaultUpdate & { dtcBrandVariantId: number; dtcBrandCode: string | null };
    const brandCode = eventData.dtcBrandCode || 'unknown';
    if (!acc[brandCode]) {
      acc[brandCode] = [];
    }
    acc[brandCode].push({ ...item, eventData });
    return acc;
  }, {}), [j1939Events]);

  const batchChunks = useMemo(() => Object.entries(groupedByBrand).flatMap(([brandCode, events]) => {
    return chunk(events, 100).map((chunkedEvents) => ({
      brandCode,
      events: chunkedEvents,
    }));
  }), [groupedByBrand]);

  const queries = useQueries({
    queries: batchChunks.map(({ brandCode, events }) => ({
      queryKey: ['batch-dtc-fault-descriptions', brandCode, events.map((e) => e.eventData.dtcBrandVariantId).sort().join(','), language],
      queryFn: async () => {
        try {
          const dtcBrandVariantIdMapping = mapValues(
            groupBy(events, (event) => event.eventData.type),
            (groupedEvents) => groupedEvents.map((event) => event.eventData.dtcBrandVariantId),
          );
          const results = await restClient.dtc.getDtcFaultDescriptionsForBrandVariants({ dtcBrandVariantIdMapping }, brandCode, language);

          const resultMap: Record<number, DtcFaultDescriptionsResponseItem | null> = {};
          results.forEach((result) => {
            resultMap[result.dtcBrandVariantId] = result;
          });

          return resultMap;
        } catch (err) {
          console.error('Batch fault description fetch failed:', err);
          const dtcBrandVariantIds = [...new Set(events.map((e) => e.eventData.dtcBrandVariantId))];
          return dtcBrandVariantIds.reduce<Record<number, null>>((acc, brandVariantId) => {
            acc[brandVariantId] = null;
            return acc;
          }, {});
        }
      },
      staleTime: Time.hours(1),
      gcTime: Time.hours(24),
      retry: false,
      enabled: events.length > 0,
    })),
  });

  const combinedData: BatchFaultDescriptionResult = queries.reduce((acc, query) => {
    if (query.data) {
      return { ...acc, ...query.data };
    }
    return acc;
  }, {});

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);

  return {
    data: combinedData,
    isLoading,
    isError,
  };
};

export const useBatchDtcSourceDescriptions = ({
  eventDataList,
}: BatchSourceDescriptionParams): {
  data: BatchSourceDescriptionResult;
  isLoading: boolean;
  isError: boolean;
} => {
  const { restClient } = useAuthenticated();
  const { i18n } = useTranslation();
  const i18nLang = i18n.resolvedLanguage?.split('-') || ['en'];
  const language = i18nLang[0].toLowerCase();

  const j1939Events = useMemo(() => eventDataList.filter(({ eventData }) => {
    return isNewEvent(eventData) &&
      eventData.dtcSourceId !== null &&
      (eventData.dtcMatchType === DtcMatchTypes.EXACT || eventData.dtcMatchType === DtcMatchTypes.J1939);
  }), [eventDataList]);

  const groupedByBrand = j1939Events.reduce<Record<string, Array<{ eventData: FaultUpdate & { dtcSourceId: number; dtcBrandCode: string | null }; thingId: string; eventId: string }>>>((acc, item) => {
    const eventData = item.eventData as FaultUpdate & { dtcSourceId: number; dtcBrandCode: string | null };
    const key = eventData.dtcBrandCode || 'null';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push({ ...item, eventData });
    return acc;
  }, {});

  const batchChunks = useMemo(() => Object.entries(groupedByBrand).flatMap(([brandCode, events]) => {
    return chunk(events, 100).map((chunkedEvents) => ({
      brandCode,
      events: chunkedEvents,
    }));
  }), [groupedByBrand]);

  const queries = useQueries({
    queries: batchChunks.map(({ brandCode, events }) => ({
      queryKey: ['batch-dtc-source-descriptions', brandCode, events.map((e) => e.eventData.dtcSourceId).sort().join(','), language],
      queryFn: async () => {
        try {
          const dtcSourceIdMapping = mapValues(
            groupBy(events, (event) => event.eventData.type),
            (groupedEvents) => groupedEvents.map((event) => event.eventData.dtcSourceId),
          );
          const results = await restClient.dtc.getDtcSourceDescriptionsForSources({ dtcSourceIdMapping }, brandCode, language);

          const resultMap: Record<number, DtcSourceDescriptionResponse | null> = {};
          results.forEach((result) => {
            resultMap[result.dtcSourceId] = result;
          });

          return resultMap;
        } catch (err) {
          console.error('Batch source description fetch failed:', err);
          const dtcSourceIds = [...new Set(events.map((e) => e.eventData.dtcSourceId))];
          return dtcSourceIds.reduce<Record<number, null>>((acc, sourceId) => {
            acc[sourceId] = null;
            return acc;
          }, {});
        }
      },
      staleTime: Time.hours(1),
      gcTime: Time.hours(24),
      retry: false,
      enabled: events.length > 0,
    })),
  });

  const combinedData: BatchSourceDescriptionResult = queries.reduce((acc, query) => {
    if (query.data) {
      return { ...acc, ...query.data };
    }
    return acc;
  }, {});

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);

  return {
    data: combinedData,
    isLoading,
    isError,
  };
};
